import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { Workspace } from '../api/types'

const STORAGE_KEY = 'indiefuture.workspace'

interface WorkspaceContextValue {
  workspace: Workspace | null
  workspaces: Workspace[]
  select: (id: string) => void
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/** Holds which of the caller's workspaces is current, remembered per browser. */
export function WorkspaceProvider({ workspaces, children }: { workspaces: Workspace[]; children: ReactNode }) {
  const [selected, setSelected] = useState<string | null>(readStored)
  const select = useCallback((id: string) => {
    setSelected(id)
    try {
      localStorage.setItem(STORAGE_KEY, id)
    } catch {
      // Storage blocked: the choice lasts for this page view.
    }
  }, [])
  const value = useMemo(
    () => ({ workspace: workspaces.find((w) => w.id === selected) ?? workspaces[0] ?? null, workspaces, select }),
    [workspaces, selected, select],
  )
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useCurrentWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext)
  if (!context) throw new Error('useCurrentWorkspace must be used inside WorkspaceProvider')
  return context
}
